// ImageRegionSelector — drag-a-rectangle region picker over the source image.
//
// Same fullscreen visual language as ImageTextSelector, but instead of running
// OCR on the crop it just returns the selected rectangle as a NORMALIZED
// bounding box ({ x, y, width, height } in 0-1, relative to the rendered image).
// Used by replace-product so the user marks WHICH object on the source image is
// swapped for the reference item (mirrors translate/remove-text region mode).

import { useState, useRef, useCallback, useEffect } from "react";
import { createPortal } from "react-dom";
import i18n from "i18next";
import { LuX } from "react-icons/lu";

export default function ImageRegionSelector({ imageUrl, onSelect, onClose }) {
  const imgRef = useRef(null);
  const [drag, setDrag] = useState(null); // { startX, startY, x, y } in viewport px

  // Esc closes.
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose?.();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const onMouseDown = useCallback((e) => {
    e.preventDefault();
    setDrag({ startX: e.clientX, startY: e.clientY, x: e.clientX, y: e.clientY });
  }, []);

  const onMouseMove = useCallback(
    (e) => {
      if (!drag) return;
      setDrag((d) => (d ? { ...d, x: e.clientX, y: e.clientY } : d));
    },
    [drag]
  );

  const onMouseUp = useCallback(() => {
    if (!drag) return;
    const sel = {
      left: Math.min(drag.startX, drag.x),
      top: Math.min(drag.startY, drag.y),
      width: Math.abs(drag.x - drag.startX),
      height: Math.abs(drag.y - drag.startY),
    };
    setDrag(null);
    if (sel.width < 5 || sel.height < 5) return; // accidental click

    const img = imgRef.current;
    if (!img) return;
    const rect = img.getBoundingClientRect();
    // Intersect selection with the rendered image, then normalise to 0-1.
    const left = Math.max(sel.left, rect.left);
    const top = Math.max(sel.top, rect.top);
    const right = Math.min(sel.left + sel.width, rect.right);
    const bottom = Math.min(sel.top + sel.height, rect.bottom);
    if (right <= left || bottom <= top) return;

    const region = {
      x: +((left - rect.left) / rect.width).toFixed(4),
      y: +((top - rect.top) / rect.height).toFixed(4),
      width: +((right - left) / rect.width).toFixed(4),
      height: +((bottom - top) / rect.height).toFixed(4),
    };
    onSelect?.(region);
  }, [drag, onSelect]);

  const selBox = drag
    ? {
        left: Math.min(drag.startX, drag.x),
        top: Math.min(drag.startY, drag.y),
        width: Math.abs(drag.x - drag.startX),
        height: Math.abs(drag.y - drag.startY),
      }
    : null;

  // Overlay document.body içine portallanır. Bu bileşen gömülü (embed) Aidea
  // Wizard panelinden de mount ediliyor; o panel bootstrap modalının
  // .modal-dialog düğümü içinde yaşıyor ve dialog transform aldığı için
  // position:fixed çocuklar viewport yerine dialog’a göre konumlanıyordu —
  // seçim dikdörtgeni ekranın başka bir yerine düşüyordu. Lightbox.jsx da
  // aynı yöntemi kullanıyor.
  return createPortal(
    <div
      onMouseDown={onMouseDown}
      onMouseMove={onMouseMove}
      onMouseUp={onMouseUp}
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,0.9)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 3000070, // above the dialog band (pageModal 3000000+) and Lightbox (3000060)
        cursor: "crosshair",
        userSelect: "none",
      }}
    >
      <img
        ref={imgRef}
        src={imageUrl}
        alt="Select region"
        draggable={false}
        style={{
          maxWidth: "92vw",
          maxHeight: "88vh",
          objectFit: "contain",
          borderRadius: 8,
          pointerEvents: "none",
        }}
      />

      {/* Selection rectangle */}
      {selBox && selBox.width > 0 && selBox.height > 0 && (
        <div
          style={{
            position: "fixed",
            left: selBox.left,
            top: selBox.top,
            width: selBox.width,
            height: selBox.height,
            border: "2px solid #22c55e",
            background: "rgba(34,197,94,0.15)",
            pointerEvents: "none",
            zIndex: 3000071,
          }}
        />
      )}

      {/* Instruction banner */}
      <div
        style={{
          position: "fixed",
          top: 18,
          left: "50%",
          transform: "translateX(-50%)",
          background: "rgba(0,0,0,0.6)",
          color: "#fff",
          padding: "8px 16px",
          borderRadius: 8,
          fontSize: 13,
          fontWeight: 600,
          backdropFilter: "blur(4px)",
          zIndex: 3000072,
          pointerEvents: "none",
        }}
      >
        {i18n.t("ReplaceProduct.DragOverObject")}
      </div>

      {/* Close */}
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          onClose?.();
        }}
        title={i18n.t("Cancel")}
        style={{
          position: "fixed",
          top: 18,
          right: 18,
          background: "rgba(255,255,255,0.15)",
          border: "none",
          borderRadius: "50%",
          width: 38,
          height: 38,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          color: "#fff",
          cursor: "pointer",
          backdropFilter: "blur(4px)",
          zIndex: 3000072,
        }}
      >
        <LuX size={18} />
      </button>
    </div>,
    document.body
  );
}
